import Communication from './Communication';
import Session from './Session';
import Logger from './Logger';

class Navigation {
	static isGetUrlInProgress = false;

	static async navigateTo(target, additionalInfo) {
		let targetUrl = await this.getNavigationUrl(target);

		if (additionalInfo && additionalInfo.queryString) {
			targetUrl = `${targetUrl}&${additionalInfo.queryString}`;
		}

		if (additionalInfo && additionalInfo.navType === 'newTab') {
			this.newTab(targetUrl);
		} else {
			this.redirect(targetUrl);
		}
	}

	static async getNavigationUrl(target) {
		const settings = Session.getSettings();
		const { Page: currentApp } = settings;
		const historyParams = {
			OnlineReportingHistory: '',
			OnlineReportingRefund: 'refund',
			OnlineReportingClaim: 'fileclaim',
			OnlineReportingScan: 'manifest',
			OnlineReportingPickup: 'pickup',
			OnlineReportingGAPickup: 'containerlabel',
			OnlineReportingAssist: 'refundassist',
		};

		if (target && target.indexOf('account_') !== -1) {
			const response = await Communication.getUrl({
				urlType: target,
			});

			if (Communication.isSuccessful(response)) {
				return this.buildUrlByStoreContext(response.data);
			} else {
				console.log('error getting navigation url for token', target);
				return null;
			}
		} else {
			const navigationObject = this.getNavObj(target);
			const {
				app,
				applicationContext,
				linkCategory,
				routePath,
				url,
				urlType,
			} = navigationObject;
			let requestObject, response;

			switch (linkCategory) {
				case 'static':
					return url;
				case 'getLink':
					requestObject = {
						urlType,
					};

					// Note: this is a workaround for ReactHistory cutover
					if (currentApp === 'History' && historyParams[urlType] !== undefined) {
						return `${__BASENAME__}${
							historyParams[urlType] ? `?eligibleFor=${historyParams[urlType]}` : ''
						}`;
					}

					// adds additional request params when needed, ex: store targets need applicationContext
					if (applicationContext) {
						requestObject.applicationContext = applicationContext;
					}

					// fetches url from getUrl call
					response = await Communication.getUrl(requestObject);

					if (Communication.isSuccessful(response)) {
						return response.data;
					} else {
						console.log('error getting navigation url');
						return null;
					}
				case 'webClientApp':
					if (app === currentApp) {
						return __BASENAME__ + routePath;
					} else {
						requestObject = {
							urlType: 'WebClientApp',
							app,
							route: routePath,
						};

						if (!this.isGetUrlInProgress) {
							this.isGetUrlInProgress = true;
							// fetches url from getUrl call
							response = await Communication.getUrl(requestObject);

							if (Communication.isSuccessful(response)) {
								this.isGetUrlInProgress = false;
								return response.data;
							} else {
								this.isGetUrlInProgress = false;
								return null;
							}
						} else {
							return null;
						}
					}
				default:
					console.log('Nav object not found for target:', target);
					return null;
			}
		}
	}

	static getNavObj(target) {
		const { domain } = Session.getBrand();
		const {
				HelpCenterBaseUrl: helpCenterBaseUrl,
				RegistrationUrl: registrationUrl,
				CutAtgStoreLinks: cutAtgStoreLinks = false,
			} = Session.getSettings(),
			navMap = {
				mail: {
					linkCategory: 'webClientApp',
					app: 'mail',
				},

				orders: {
					linkCategory: 'webClientApp',
					app: 'orders',
				},

				products: {
					linkCategory: 'webClientApp',
					app: 'products',
				},

				inbounds: {
					linkCategory: 'webClientApp',
					app: 'inbounds',
				},

				contacts: {
					linkCategory: 'webClientApp',
					app: 'contacts',
				},

				marketing: {
					linkCategory: 'webClientApp',
					app: 'marketing',
				},

				history: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingHistory',
				},

				searchPrintHistory: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingHistory',
				},

				requestPostageRefund: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingRefund',
				},

				fileInsuranceClaim: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingClaim',
				},

				createScanForm: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingScan',
				},

				scheduleUspsPickUp: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingPickup',
				},

				createContainerLabel: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingGAPickup',
				},

				refundAssist: {
					linkCategory: 'getLink',
					urlType: 'OnlineReportingAssist',
				},

				reports: {
					linkCategory: 'getLink',
					urlType: 'ReportsBalances',
				},

				supplies: {
					linkCategory: 'getLink',
					urlType: 'StoreHome',
				},

				netstamps: {
					linkCategory: 'getLink',
					urlType: cutAtgStoreLinks ? 'StoreNetStamps' : 'StoreCategory',
					applicationContext: cutAtgStoreLinks ? '' : 'catnetstamps',
				},

				labels: {
					linkCategory: 'getLink',
					urlType: cutAtgStoreLinks ? 'StoreLabels' : 'StoreCategory',
					applicationContext: cutAtgStoreLinks ? '' : 'catlabelsnetstamps',
				},

				shippingSupplies: {
					linkCategory: 'getLink',
					urlType: cutAtgStoreLinks ? 'StoreShippingSupplies' : 'StoreCategory',
					applicationContext: cutAtgStoreLinks ? '' : 'cat20610017',
				},

				envelopes: {
					linkCategory: 'getLink',
					urlType: cutAtgStoreLinks ? 'StoreEnvelopes' : 'StoreCategory',
					applicationContext: cutAtgStoreLinks ? '' : 'cat20610015',
				},

				freeUspsSupplies: {
					linkCategory: 'getLink',
					urlType: cutAtgStoreLinks ? 'StoreFreeUSPSSupplies' : 'StoreCategory',
					applicationContext: cutAtgStoreLinks ? '' : 'cat653010023',
				},

				storeChangePlan: {
					linkCategory: 'getLink',
					urlType: 'StoreChangePlan',
				},

				helpChatbot: {
					linkCategory: 'static',
				},

				helpCenter: {
					linkCategory: 'static',
					url: helpCenterBaseUrl,
				},

				editPaymentMethod: {
					linkCategory: 'getLink',
					urlType: 'StoreDefaultPaymentMethods',
				},

				autoFund: {
					linkCategory: 'getLink',
					urlType: 'StoreAutoBuy',
				},

				earnCredit: {
					linkCategory: 'getLink',
					urlType: 'StoreReferAFriend',
				},

				viewPurchaseHistory: {
					linkCategory: 'getLink',
					urlType: 'ReportsBalances',
				},

				changePaymentMethod: {
					linkCategory: 'getLink',
					urlType: 'StorePaymentMethods',
				},

				paymentSettings: {
					linkCategory: 'getLink',
					urlType: 'StoreDefaultPaymentMethods',
				},

				manageAccount: {
					linkCategory: 'getLink',
					urlType: 'StoreAccount',
				},

				legalTerms: {
					linkCategory: 'static',
					url: `https://account.web${domain}/terms/legallist`,
				},

				register: {
					linkCategory: 'static',
					url: registrationUrl,
				},

				storeHome: {
					linkCategory: 'getLink',
					urlType: 'StoreHome',
				},

				// TODO: Visit the URLs since they're hard coded to prod. We'll need to get them from Session so the brand doesn't get used here
				privacyPolicy: {
					linkCategory: 'static',
					url: `https://www${domain}/privacy-policy/`,
				},

				doNotSell: {
					linkCategory: 'static',
					url: `https://www${domain}/privacy-policy/do-not-sell-my-personal-information/`,
				},

				storeAccount: {
					linkCategory: 'getLink',
					urlType: 'StoreAccount',
				},

				winClientDownload: {
					linkCategory: 'static',
					url: 'https://www.stamps.com/site/resources/stamps.exe',
				},

				homePage: {
					linkCategory: 'static',
					url: `https://www${domain}`,
				},

				trackAPackage: {
					linkCategory: 'static',
					url: `https://www${domain}/tracking`,
				},

				accountverification: {
					linkCategory: 'getLink',
					urlType: 'AccountVerification',
				},

				signin: {
					linkCategory: 'static',
					url: `${Communication.accountApiUrlPrefix}/auth/startAuth?`,
				},

				signout: {
					linkCategory: 'static',
					url: `${Communication.accountApiUrlPrefix}/auth/clearAuth`,
				},
			};

		return navMap[target];
	}

	static redirect(url) {
		if (url) {
			document.location = url;
		}
	}

	static newTab(url) {
		window.open(url);
	}

	/**
	 * Modify the URL that comes back from GetUrl based on storeContext.
	 * @param {string} url
	 * @return {string}
	 */
	static buildUrlByStoreContext(url) {
		const settings = Session.getSettings();
		const { IsInWinClient, isInWebStore, isInAtgStore } = settings;

		if (IsInWinClient === true) {
			return url.replace(/\/\/customer./i, '//clientstore.');
		} else if (isInWebStore === true || isInAtgStore === true) {
			return url.replace(/\/\/customer./i, '//store.');
		} else {
			return url;
		}
	}

	static async getTransferAuth() {
		const response = await Communication.getUrl({ urlType: 'transferAuth' });

		if (Communication.isSuccessful(response)) {
			return response.data;
		} else {
			Logger.logToServer({ category: 'transferAuth', error: response });
			return '';
		}
	}

	static async redirectToAccountVerify(app) {
		const target = 'accountverification';
		const { Page = '' } = Session.getSettings();

		await this.navigateTo(target, { queryString: `redirectApp=${Page.toLowerCase()}` });
	}
}

export default Navigation;
